import React, { useState, useEffect } from 'react';
import { useAuth, UserRole } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Plus, Users, UserPlus, UserMinus, Search, Phone,
    AlertTriangle, Heart, PawPrint, Home, UserCheck,
    Activity, Edit, Building2, Hotel, ArrowRightCircle,
    Trash2, Target, FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const evacuationStatuses = [
    { value: 'heberge_centre', label: 'Heberge en centre', color: 'info', icon: Building2 },
    { value: 'en_cours', label: 'En cours d\'evacuation', color: 'warning', icon: Activity },
    { value: 'evacue', label: 'Evacue', color: 'success', icon: UserCheck },
    { value: 'sortie_domicile', label: 'Sortie - Rentre chez soi', color: 'success', icon: Home },
    { value: 'sortie_hotel', label: 'Sortie - Hotel', color: 'info', icon: Hotel },
    { value: 'sortie_externe', label: 'Sortie - Hebergement externe', color: 'warning', icon: ArrowRightCircle }
];

const psychologicalStates = [
    { value: 'stable', label: 'Stable', color: 'success' },
    { value: 'choque', label: 'Choque', color: 'warning' },
    { value: 'critique', label: 'Critique', color: 'critical' }
];

const damageLevels = [
    { value: 'intacte', label: 'Intacte', color: 'success' },
    { value: 'partiellement_detruite', label: 'Partiellement detruite', color: 'warning' },
    { value: 'detruite', label: 'Detruite', color: 'critical' }
];

const emptyFicheVictime = {
    victime_numero: '',
    sexe: '',
    heure_prise_en_charge: '',
    circonstance: '',
    signes: '',
    constantes: [],
    gestes_effectues: '',
    evolution: '',
    devenir: [],
    douleur: 0,
    lesions: [],
    lesions_notes: ''
};

const emptyConstante = { heure: '', fc: '', ta: '', fr: '', sat: '', gly: '' };

const emptyForm = {
    first_name: '', last_name: '', birth_date: '', phone: '',
    origin_address: '', emergency_contact: '',
    evacuation_status: 'heberge_centre',
    has_animals: false, animal_details: '', animal_has_cage: false,
    psychological_state: 'stable', needs_cump: false,
    is_with_family: false, family_members: [],
    is_at_risk: false, risk_details: '', medical_notes: '',
    rescue_assessment: '', damage_level: 'intacte',
    sortie_details: '', center_id: '', incident_id: '',
    mission_id: '', mission_name: '',
    registration_datetime: '',
    fiche_victime: { ...emptyFicheVictime, constantes: [], devenir: [], lesions: [] }
};

const isoToFr = (iso) => {
    const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || '');
};
const frToIso = (fr) => {
    const m = (fr || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (fr || '');
};
const formatDateTyping = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const PersonsPage = () => {
    const { token, user, canManagePersons, isGymnaseAdmin, isGymnaseChefPoste } = useAuth();
    const [persons, setPersons] = useState([]);
    const [centers, setCenters] = useState([]);
    const [missions, setMissions] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPerson, setEditingPerson] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [evacuationFilter, setEvacuationFilter] = useState('all');
    const [formData, setFormData] = useState({...emptyForm});
    const [familyMemberInput, setFamilyMemberInput] = useState({ name: '', relationship: '' });
    const [eventActive, setEventActive] = useState(true);
    const [viewPerson, setViewPerson] = useState(null);

    const isChef = user?.role === UserRole.CHEF_DISPOSITIF;
    const canRegisterPersons = canManagePersons || user?.role === UserRole.SECOURISTE;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [personsRes, centersRes, incidentsRes, eventRes, missionsRes] = await Promise.all([
                axios.get(`${API}/persons`, { headers }),
                axios.get(`${API}/care-centers`, { headers }),
                axios.get(`${API}/incidents?active_only=true`, { headers }),
                axios.get(`${API}/event/status`, { headers }),
                axios.get(`${API}/missions`, { headers }).catch(() => ({ data: [] }))
            ]);
            setPersons(personsRes.data);
            setCenters(centersRes.data);
            setIncidents(incidentsRes.data);
            setEventActive(eventRes.data.event_active);
            setMissions(missionsRes.data);
            if ((isGymnaseAdmin || isGymnaseChefPoste) && user?.assigned_center_id) {
                setFormData(prev => ({ ...prev, center_id: user.assigned_center_id }));
            }
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handlePurge = async () => {
        if (!window.confirm('ATTENTION : Cette action supprimera TOUTES les personnes du registre. Continuer ?')) return;
        if (!window.confirm('Etes-vous vraiment sur ? Cette action est irreversible.')) return;
        try {
            const res = await axios.delete(`${API}/persons/purge`, { headers });
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, birth_date: frToIso(formData.birth_date) };
            if (editingPerson) {
                await axios.put(`${API}/persons/${editingPerson.id}`, payload, { headers });
                toast.success('Personne mise a jour');
            } else {
                await axios.post(`${API}/persons`, payload, { headers });
                toast.success('Personne enregistree');
            }
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleEdit = (person) => {
        setEditingPerson(person);
        const fv = person.fiche_victime || {};
        setFormData({
            first_name: person.first_name, last_name: person.last_name,
            birth_date: isoToFr(person.birth_date || ''), phone: person.phone || '',
            origin_address: person.origin_address || '',
            emergency_contact: person.emergency_contact || '',
            evacuation_status: person.evacuation_status,
            has_animals: person.has_animals, animal_details: person.animal_details || '',
            animal_has_cage: person.animal_has_cage || false,
            psychological_state: person.psychological_state,
            needs_cump: person.needs_cump || false,
            is_with_family: person.is_with_family,
            family_members: person.family_members || [],
            is_at_risk: person.is_at_risk, risk_details: person.risk_details || '',
            medical_notes: person.medical_notes || '',
            rescue_assessment: person.rescue_assessment || '',
            damage_level: person.damage_level || 'intacte',
            sortie_details: person.sortie_details || '',
            center_id: person.center_id || '', incident_id: person.incident_id || '',
            mission_id: person.mission_id || '', mission_name: person.mission_name || '',
            fiche_victime: {
                victime_numero: fv.victime_numero || '',
                sexe: fv.sexe || '',
                heure_prise_en_charge: fv.heure_prise_en_charge || '',
                circonstance: fv.circonstance || '',
                signes: fv.signes || '',
                constantes: Array.isArray(fv.constantes) ? fv.constantes : (fv.constantes && fv.constantes.fc ? [fv.constantes] : []),
                gestes_effectues: fv.gestes_effectues || '',
                evolution: fv.evolution || '',
                devenir: fv.devenir || [],
                douleur: fv.douleur ?? 0,
                lesions: fv.lesions || [],
                lesions_notes: fv.lesions_notes || ''
            }
        });
        setDialogOpen(true);
    };

    const handleCheckout = async (personId) => {
        if (!window.confirm('Confirmer la sortie ?')) return;
        try {
            await axios.put(`${API}/persons/${personId}/checkout`, {}, { headers });
            toast.success('Sortie enregistree');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const resetForm = () => {
        setFormData({...emptyForm});
        setEditingPerson(null);
        if ((isGymnaseAdmin || isGymnaseChefPoste) && user?.assigned_center_id) {
            setFormData(prev => ({ ...prev, center_id: user.assigned_center_id }));
        }
    };

    const addFamilyMember = () => {
        if (familyMemberInput.name) {
            setFormData(prev => ({
                ...prev,
                family_members: [...prev.family_members, { ...familyMemberInput }]
            }));
            setFamilyMemberInput({ name: '', relationship: '' });
        }
    };

    const removeFamilyMember = (index) => {
        setFormData(prev => ({
            ...prev,
            family_members: prev.family_members.filter((_, i) => i !== index)
        }));
    };

    // Filters
    const filtered = persons.filter(p => {
        const matchSearch = !searchTerm || 
            `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'all' || p.status === statusFilter;
        const matchEvac = evacuationFilter === 'all' || p.evacuation_status === evacuationFilter;
        return matchSearch && matchStatus && matchEvac;
    });

    // Counter: expected vs actual
    const totalCapacity = centers.reduce((s, c) => s + c.capacity, 0);
    const totalPresent = persons.filter(p => p.status === 'accueilli').length;
    const ecart = totalCapacity - totalPresent;

    if (loading) {
        return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
    }

    const isSortie = formData.evacuation_status?.startsWith('sortie_');

    return (
        <div className="space-y-6" data-testid="persons-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="page-header">
                    <h1 className="text-3xl font-bold text-white">Registre des personnes</h1>
                    <p className="page-subtitle">Suivi des personnes prises en charge</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {isChef && !eventActive && persons.length > 0 && (
                        <Button onClick={handlePurge} variant="destructive" className="bg-red-600 hover:bg-red-700" data-testid="purge-persons-btn">
                            <Trash2 className="w-4 h-4 mr-2" /> Purger le registre ({persons.length})
                        </Button>
                    )}
                    {canRegisterPersons && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="tactical-btn" data-testid="add-person-btn">
                                <Plus className="w-4 h-4 mr-2" /> Nouvelle personne
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-semibold text-white">
                                    {editingPerson ? 'Modifier la personne' : 'Enregistrer une personne'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Identity */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Prenom *</Label>
                                        <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} className="tactical-input" required data-testid="person-firstname" />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Nom *</Label>
                                        <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} className="tactical-input" required data-testid="person-lastname" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Date de naissance</Label>
                                        <Input type="text" inputMode="numeric" placeholder="JJ/MM/AAAA" maxLength={10} value={formData.birth_date} onChange={(e) => setFormData({...formData, birth_date: formatDateTyping(e.target.value)})} className="tactical-input" data-testid="person-birthdate" />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Telephone</Label>
                                        <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="tactical-input" />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Contact d'urgence</Label>
                                        <Input value={formData.emergency_contact} onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})} className="tactical-input" />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Adresse de provenance</Label>
                                    <Input value={formData.origin_address} onChange={(e) => setFormData({...formData, origin_address: e.target.value})} className="tactical-input" />
                                </div>

                                {/* Status */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Statut</Label>
                                        <Select value={formData.evacuation_status} onValueChange={(v) => setFormData({...formData, evacuation_status: v, sortie_details: ''})}>
                                            <SelectTrigger className="tactical-input" data-testid="person-evac-status"><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {evacuationStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Niveau d'atteinte du logement</Label>
                                        <Select value={formData.damage_level} onValueChange={(v) => setFormData({...formData, damage_level: v})}>
                                            <SelectTrigger className="tactical-input" data-testid="person-damage"><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {damageLevels.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {isSortie && (
                                    <div className="form-group p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                        <Label className="form-label">Precision sur la sortie</Label>
                                        <Input value={formData.sortie_details} onChange={(e) => setFormData({...formData, sortie_details: e.target.value})} className="tactical-input" placeholder="Nom hotel, adresse hebergeur..." data-testid="person-sortie-details" />
                                    </div>
                                )}

                                {formData.evacuation_status === 'heberge_centre' && (
                                    <div className="form-group p-3 bg-cyan-500/5 rounded border border-cyan-500/20">
                                        <Label className="form-label flex items-center gap-2"><Building2 className="w-4 h-4 text-cyan-400" /> Centre CARE</Label>
                                        <Select value={formData.center_id} onValueChange={(v) => setFormData({...formData, center_id: v})}>
                                            <SelectTrigger className="tactical-input" data-testid="person-center-select"><SelectValue placeholder="Selectionner un centre" /></SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.current_count || 0}/{c.capacity || '?'})</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* Mission */}
                                <div className="form-group p-3 bg-amber-500/5 rounded border border-amber-500/20">
                                    <Label className="form-label flex items-center gap-2"><Target className="w-4 h-4 text-amber-400" /> Mission associee</Label>
                                    <Select value={formData.mission_id || 'none'} onValueChange={(v) => {
                                        const m = missions.find(x => x.id === v);
                                        setFormData({...formData, mission_id: v === 'none' ? '' : v, mission_name: m ? m.title : ''});
                                    }}>
                                        <SelectTrigger className="tactical-input" data-testid="person-mission-select"><SelectValue placeholder="Aucune mission" /></SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            <SelectItem value="none">Aucune mission</SelectItem>
                                            {missions.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Psy + CUMP */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Etat psychologique</Label>
                                        <Select value={formData.psychological_state} onValueChange={(v) => setFormData({...formData, psychological_state: v, needs_cump: v === 'critique' || v === 'choque' ? formData.needs_cump : false})}>
                                            <SelectTrigger className="tactical-input" data-testid="person-psy"><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {psychologicalStates.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {(formData.psychological_state === 'choque' || formData.psychological_state === 'critique') && (
                                        <div className="form-group flex items-end">
                                            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg w-full">
                                                <Checkbox checked={formData.needs_cump} onCheckedChange={(c) => setFormData({...formData, needs_cump: c})} data-testid="person-cump" />
                                                <Label className="text-red-400 font-medium text-sm">Besoin CUMP</Label>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Animals */}
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <Checkbox checked={formData.has_animals} onCheckedChange={(c) => setFormData({...formData, has_animals: c, animal_has_cage: false, animal_details: ''})} data-testid="person-animals" />
                                        <Label>Animaux</Label>
                                    </div>
                                    {formData.has_animals && (
                                        <>
                                            <div className="flex items-center gap-2">
                                                <Checkbox checked={formData.animal_has_cage} onCheckedChange={(c) => setFormData({...formData, animal_has_cage: c})} data-testid="person-cage" />
                                                <Label>Avec cage</Label>
                                            </div>
                                            <Input value={formData.animal_details} onChange={(e) => setFormData({...formData, animal_details: e.target.value})} className="tactical-input flex-1" placeholder="Type, nombre..." />
                                        </>
                                    )}
                                </div>

                                {/* Risk + Family */}
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <Checkbox checked={formData.is_at_risk} onCheckedChange={(c) => setFormData({...formData, is_at_risk: c})} data-testid="person-risk" />
                                        <Label>Personne a risque</Label>
                                    </div>
                                    {formData.is_at_risk && (
                                        <Input value={formData.risk_details} onChange={(e) => setFormData({...formData, risk_details: e.target.value})} className="tactical-input flex-1" placeholder="Details..." />
                                    )}
                                </div>

                                {/* ===== FICHE VICTIME ===== */}
                                <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-4" data-testid="fiche-victime-section">
                                    <h3 className="text-base font-semibold text-blue-400 flex items-center gap-2">
                                        <Heart className="w-4 h-4" /> Fiche Victime
                                    </h3>

                                    {/* Ligne 1: N° victime (auto), Sexe, Heure PEC */}
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="form-group">
                                            <Label className="form-label text-xs">N° Victime {!editingPerson && <span className="text-zinc-500">(auto)</span>}</Label>
                                            <Input value={formData.fiche_victime?.victime_numero || ''} readOnly disabled className="tactical-input bg-zinc-800/60 text-zinc-400 cursor-not-allowed" data-testid="fv-numero" placeholder={editingPerson ? '' : 'Genere a l\'enregistrement'} />
                                        </div>
                                        <div className="form-group">
                                            <Label className="form-label text-xs">Sexe</Label>
                                            <Select value={formData.fiche_victime?.sexe || ''} onValueChange={(v) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), sexe: v}})}>
                                                <SelectTrigger className="tactical-input" data-testid="fv-sexe"><SelectValue placeholder="--" /></SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    <SelectItem value="M">Masculin</SelectItem>
                                                    <SelectItem value="F">Feminin</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="form-group">
                                            <Label className="form-label text-xs">Heure prise en charge</Label>
                                            <Input type="time" value={formData.fiche_victime?.heure_prise_en_charge || ''} onChange={(e) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), heure_prise_en_charge: e.target.value}})} className="tactical-input" data-testid="fv-heure-pec" />
                                        </div>
                                    </div>

                                    {/* Circonstance */}
                                    <div className="form-group">
                                        <Label className="form-label text-xs">Circonstance</Label>
                                        <Textarea value={formData.fiche_victime?.circonstance || ''} onChange={(e) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), circonstance: e.target.value}})} className="tactical-input" rows={2} data-testid="fv-circonstance" />
                                    </div>

                                    {/* Signes */}
                                    <div className="form-group">
                                        <Label className="form-label text-xs">Signes</Label>
                                        <Textarea value={formData.fiche_victime?.signes || ''} onChange={(e) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), signes: e.target.value}})} className="tactical-input" rows={2} data-testid="fv-signes" />
                                    </div>

                                    {/* Tableau des constantes - historique */}
                                    <div className="form-group" data-testid="fv-constantes-section">
                                        <div className="flex items-center justify-between">
                                            <Label className="form-label text-xs">Constantes vitales</Label>
                                            <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10" 
                                                onClick={() => {
                                                    const now = new Date();
                                                    const heure = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
                                                    const fv = formData.fiche_victime || {};
                                                    const list = Array.isArray(fv.constantes) ? fv.constantes : [];
                                                    setFormData({...formData, fiche_victime: {...fv, constantes: [...list, { ...emptyConstante, heure }]}});
                                                }}
                                                data-testid="fv-add-constante">
                                                <Plus className="w-3 h-3 mr-1" /> Nouveau releve
                                            </Button>
                                        </div>
                                        {/* Table of constantes */}
                                        {(formData.fiche_victime?.constantes || []).length > 0 && (
                                            <div className="overflow-x-auto mt-2 rounded border border-zinc-700/50">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="bg-zinc-800/80 text-zinc-400">
                                                            <th className="px-2 py-1.5 text-left font-medium">Heure</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">FC</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">TA</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">FR</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">SAT</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">Gly</th>
                                                            <th className="px-2 py-1.5 w-8"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(formData.fiche_victime?.constantes || []).map((c, idx) => (
                                                            <tr key={idx} className="border-t border-zinc-700/30" data-testid={`fv-constante-row-${idx}`}>
                                                                <td className="px-1 py-1">
                                                                    <Input type="time" value={c.heure || ''} onChange={(e) => {
                                                                        const list = [...(formData.fiche_victime?.constantes || [])];
                                                                        list[idx] = {...list[idx], heure: e.target.value};
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="tactical-input h-7 text-xs w-20" />
                                                                </td>
                                                                <td className="px-1 py-1">
                                                                    <Input value={c.fc || ''} onChange={(e) => {
                                                                        const list = [...(formData.fiche_victime?.constantes || [])];
                                                                        list[idx] = {...list[idx], fc: e.target.value};
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="tactical-input h-7 text-xs w-16" placeholder="--" data-testid={`fv-fc-${idx}`} />
                                                                </td>
                                                                <td className="px-1 py-1">
                                                                    <Input value={c.ta || ''} onChange={(e) => {
                                                                        const list = [...(formData.fiche_victime?.constantes || [])];
                                                                        list[idx] = {...list[idx], ta: e.target.value};
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="tactical-input h-7 text-xs w-20" placeholder="--/--" data-testid={`fv-ta-${idx}`} />
                                                                </td>
                                                                <td className="px-1 py-1">
                                                                    <Input value={c.fr || ''} onChange={(e) => {
                                                                        const list = [...(formData.fiche_victime?.constantes || [])];
                                                                        list[idx] = {...list[idx], fr: e.target.value};
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="tactical-input h-7 text-xs w-14" placeholder="--" data-testid={`fv-fr-${idx}`} />
                                                                </td>
                                                                <td className="px-1 py-1">
                                                                    <Input value={c.sat || ''} onChange={(e) => {
                                                                        const list = [...(formData.fiche_victime?.constantes || [])];
                                                                        list[idx] = {...list[idx], sat: e.target.value};
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="tactical-input h-7 text-xs w-14" placeholder="--" data-testid={`fv-sat-${idx}`} />
                                                                </td>
                                                                <td className="px-1 py-1">
                                                                    <Input value={c.gly || ''} onChange={(e) => {
                                                                        const list = [...(formData.fiche_victime?.constantes || [])];
                                                                        list[idx] = {...list[idx], gly: e.target.value};
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="tactical-input h-7 text-xs w-14" placeholder="--" data-testid={`fv-gly-${idx}`} />
                                                                </td>
                                                                <td className="px-1 py-1">
                                                                    <button type="button" onClick={() => {
                                                                        const list = (formData.fiche_victime?.constantes || []).filter((_, i) => i !== idx);
                                                                        setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), constantes: list}});
                                                                    }} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`fv-remove-constante-${idx}`}>
                                                                        <Trash2 className="w-3 h-3" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        {(formData.fiche_victime?.constantes || []).length === 0 && (
                                            <p className="text-xs text-zinc-500 mt-1">Aucun releve. Cliquez sur "Nouveau releve" pour ajouter.</p>
                                        )}
                                    </div>

                                    {/* Gestes effectues */}
                                    <div className="form-group">
                                        <Label className="form-label text-xs">Gestes effectues</Label>
                                        <Textarea value={formData.fiche_victime?.gestes_effectues || ''} onChange={(e) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), gestes_effectues: e.target.value}})} className="tactical-input" rows={2} data-testid="fv-gestes" />
                                    </div>

                                    {/* Echelle de douleur 0-10 */}
                                    <div className="form-group">
                                        <Label className="form-label text-xs">Echelle de douleur : {formData.fiche_victime?.douleur ?? 0}/10</Label>
                                        <div className="flex items-center gap-1 mt-1" data-testid="fv-douleur">
                                            {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
                                                const val = formData.fiche_victime?.douleur ?? 0;
                                                const bg = n <= 2 ? 'bg-blue-500' : n <= 4 ? 'bg-green-500' : n <= 6 ? 'bg-yellow-500' : n <= 8 ? 'bg-orange-500' : 'bg-red-500';
                                                return (
                                                    <button key={n} type="button" onClick={() => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), douleur: n}})}
                                                        className={`w-7 h-7 rounded text-xs font-bold transition-all ${n <= val ? `${bg} text-white` : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'}`}
                                                        data-testid={`fv-douleur-${n}`}>
                                                        {n}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Lesions P-B-T-C */}
                                    <div className="form-group">
                                        <Label className="form-label text-xs">Lesions (P-B-T-C)</Label>
                                        <div className="flex flex-wrap gap-3 mt-1">
                                            {[
                                                { value: 'plaie', label: 'Plaie (P)' },
                                                { value: 'brulure', label: 'Brulure (B)' },
                                                { value: 'traumatisme', label: 'Traumatisme (T)' },
                                                { value: 'contracture', label: 'Contracture (C)' }
                                            ].map(l => (
                                                <div key={l.value} className="flex items-center gap-1.5">
                                                    <Checkbox
                                                        checked={(formData.fiche_victime?.lesions || []).includes(l.value)}
                                                        onCheckedChange={(c) => {
                                                            const current = formData.fiche_victime?.lesions || [];
                                                            const updated = c ? [...current, l.value] : current.filter(x => x !== l.value);
                                                            setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), lesions: updated}});
                                                        }}
                                                        data-testid={`fv-lesion-${l.value}`}
                                                    />
                                                    <Label className="text-xs">{l.label}</Label>
                                                </div>
                                            ))}
                                        </div>
                                        <Input value={formData.fiche_victime?.lesions_notes || ''} onChange={(e) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), lesions_notes: e.target.value}})} className="tactical-input mt-2" placeholder="Precisions sur les lesions..." data-testid="fv-lesions-notes" />
                                    </div>

                                    {/* Evolution */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="form-group">
                                            <Label className="form-label text-xs">Evolution</Label>
                                            <div className="flex flex-col gap-2 mt-1">
                                                {[
                                                    { value: 'amelioration', label: 'Amelioration', color: 'text-green-400' },
                                                    { value: 'inchange', label: 'Inchange', color: 'text-amber-400' },
                                                    { value: 'aggravation', label: 'Aggravation', color: 'text-red-400' }
                                                ].map(ev => (
                                                    <div key={ev.value} className="flex items-center gap-2">
                                                        <Checkbox
                                                            checked={formData.fiche_victime?.evolution === ev.value}
                                                            onCheckedChange={(c) => setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), evolution: c ? ev.value : ''}})}
                                                            data-testid={`fv-evolution-${ev.value}`}
                                                        />
                                                        <Label className={`text-xs ${ev.color}`}>{ev.label}</Label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Devenir */}
                                        <div className="form-group">
                                            <Label className="form-label text-xs">Devenir</Label>
                                            <div className="flex flex-col gap-2 mt-1">
                                                {[
                                                    { value: 'ps', label: 'PS (Poste de Secours)' },
                                                    { value: 'repartie', label: 'Repartie' },
                                                    { value: 'evac', label: 'Evacuation' },
                                                    { value: 'medecin', label: 'Medecin' }
                                                ].map(d => (
                                                    <div key={d.value} className="flex items-center gap-2">
                                                        <Checkbox
                                                            checked={(formData.fiche_victime?.devenir || []).includes(d.value)}
                                                            onCheckedChange={(c) => {
                                                                const current = formData.fiche_victime?.devenir || [];
                                                                const updated = c ? [...current, d.value] : current.filter(x => x !== d.value);
                                                                setFormData({...formData, fiche_victime: {...(formData.fiche_victime || {}), devenir: updated}});
                                                            }}
                                                            data-testid={`fv-devenir-${d.value}`}
                                                        />
                                                        <Label className="text-xs">{d.label}</Label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Medical + center + incident */}
                                <div className="form-group">
                                    <Label className="form-label">Notes medicales</Label>
                                    <Textarea value={formData.medical_notes} onChange={(e) => setFormData({...formData, medical_notes: e.target.value})} className="tactical-input" placeholder="Allergies, traitements..." rows={2} />
                                </div>

                                {!editingPerson && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="form-group">
                                            <Label className="form-label">Centre CARE</Label>
                                            <Select value={formData.center_id} onValueChange={(v) => setFormData({...formData, center_id: v})}>
                                                <SelectTrigger className="tactical-input"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="form-group">
                                            <Label className="form-label">Incident lie</Label>
                                            <Select value={formData.incident_id} onValueChange={(v) => setFormData({...formData, incident_id: v})}>
                                                <SelectTrigger className="tactical-input"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    {incidents.map(i => <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                )}

                                {!editingPerson && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="form-group">
                                            <Label className="form-label">Mission associee</Label>
                                            <Select value={formData.mission_id} onValueChange={(v) => {
                                                const mission = missions.find(m => m.id === v);
                                                setFormData({...formData, mission_id: v, mission_name: mission?.title || ''});
                                            }}>
                                                <SelectTrigger className="tactical-input"><SelectValue placeholder="Aucune" /></SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    {missions.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="form-group">
                                            <Label className="form-label flex items-center gap-2">
                                                Saisie differee (date/heure)
                                                <span className="text-[10px] text-zinc-500 font-normal">(optionnel)</span>
                                            </Label>
                                            <input
                                                type="datetime-local"
                                                value={formData.registration_datetime}
                                                onChange={(e) => setFormData({...formData, registration_datetime: e.target.value})}
                                                className="tactical-input w-full px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                                                data-testid="registration-datetime-input"
                                            />
                                            <p className="text-[10px] text-zinc-500 mt-1">Laisser vide = maintenant. Remplir pour antidater.</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>Annuler</Button>
                                    <Button type="submit" className="tactical-btn" data-testid="person-submit-btn">
                                        {editingPerson ? 'Mettre a jour' : 'Enregistrer'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card className="stat-card border-l-blue-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{totalPresent}</p>
                        <p className="stat-label">Prises en charge</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-purple-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{totalCapacity}</p>
                        <p className="stat-label">Capacite prevue</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-cyan-500">
                    <CardContent className="p-0">
                        <p className={`stat-value ${ecart < 0 ? 'text-red-400' : 'text-green-400'}`}>{ecart >= 0 ? `+${ecart}` : ecart}</p>
                        <p className="stat-label">Ecart places</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-amber-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{persons.filter(p => p.evacuation_status === 'heberge_centre').length}</p>
                        <p className="stat-label">Heberges</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-red-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{persons.filter(p => p.needs_cump).length}</p>
                        <p className="stat-label">Besoin CUMP</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-green-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{persons.filter(p => p.evacuation_status?.startsWith('sortie_')).length}</p>
                        <p className="stat-label">Sortis</p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="tactical-input pl-10" placeholder="Rechercher..." data-testid="person-search" />
                </div>
                <Select value={evacuationFilter} onValueChange={setEvacuationFilter}>
                    <SelectTrigger className="tactical-input w-[200px]"><SelectValue placeholder="Statut" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        {evacuationStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <Card className="content-card">
                <CardHeader className="card-header">
                    <CardTitle className="card-title flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-500" /> Personnes enregistrees ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">N°</TableHead>
                                <TableHead className="text-zinc-400">Nom</TableHead>
                                <TableHead className="text-zinc-400">Statut</TableHead>
                                <TableHead className="text-zinc-400 hidden sm:table-cell">Etat psy.</TableHead>
                                <TableHead className="text-zinc-400 hidden sm:table-cell">Atteinte</TableHead>
                                <TableHead className="text-zinc-400 hidden md:table-cell">Animaux</TableHead>
                                <TableHead className="text-zinc-400 hidden md:table-cell">Entree</TableHead>
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map(person => {
                                const evacConfig = evacuationStatuses.find(s => s.value === person.evacuation_status) || evacuationStatuses[0];
                                const psyConfig = psychologicalStates.find(s => s.value === person.psychological_state) || psychologicalStates[0];
                                const dmgConfig = damageLevels.find(d => d.value === person.damage_level) || damageLevels[0];
                                return (
                                    <TableRow key={person.id} className="border-zinc-800 cursor-pointer hover:bg-zinc-800/50 transition-colors" data-testid={`person-row-${person.id}`} onClick={() => setViewPerson(person)}>
                                        <TableCell>
                                            <span className="font-mono text-xs text-blue-400 font-semibold" data-testid={`person-numero-${person.id}`}>
                                                {person.fiche_victime?.victime_numero || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <span className="font-medium text-white">{person.first_name} {person.last_name}</span>
                                                {person.is_at_risk && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-400" />}
                                                {person.needs_cump && <span className="ml-1 px-1 text-[10px] bg-red-500/20 text-red-400 rounded">CUMP</span>}
                                            </div>
                                            {person.origin_address && <p className="text-xs text-zinc-500">{person.origin_address}</p>}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`status-badge ${evacConfig.color}`} data-testid={`person-status-${person.id}`}>
                                                {evacConfig.label}
                                            </span>
                                            {person.sortie_details && <p className="text-xs text-zinc-500 mt-0.5">{person.sortie_details}</p>}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell">
                                            <span className={`status-badge ${psyConfig.color}`}>{psyConfig.label}</span>
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell">
                                            <span className={`status-badge ${dmgConfig.color}`}>{dmgConfig.label}</span>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            {person.has_animals ? (
                                                <span className="flex items-center gap-1 text-amber-400 text-sm">
                                                    <PawPrint className="w-3 h-3" /> Oui {person.animal_has_cage ? '(cage)' : '(sans cage)'}
                                                </span>
                                            ) : <span className="text-zinc-500 text-sm">Non</span>}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell text-zinc-400 text-xs font-mono">
                                            {new Date(person.entry_time).toLocaleString('fr-FR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}
                                        </TableCell>
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                {canManagePersons && (
                                                    <>
                                                        <Button size="sm" variant="ghost" onClick={() => handleEdit(person)} className="tactical-btn-ghost" data-testid={`edit-person-${person.id}`}>
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <a href={`${API}/export/fiche-victime/${person.id}?token=${token}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="inline-flex items-center justify-center h-8 w-8 rounded-md text-cyan-400 hover:bg-cyan-500/10"
                                                            data-testid={`export-fiche-${person.id}`}
                                                            onClick={(e) => e.stopPropagation()}>
                                                            <FileDown className="w-4 h-4" />
                                                        </a>
                                                        {person.status === 'accueilli' && (
                                                            <Button size="sm" variant="ghost" onClick={() => handleCheckout(person.id)} className="text-amber-500 hover:bg-amber-500/10" data-testid={`checkout-person-${person.id}`}>
                                                                <UserMinus className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        Aucune personne enregistree
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Dialog Visualisation Bilan */}
            <Dialog open={!!viewPerson} onOpenChange={(open) => { if (!open) setViewPerson(null); }}>
                <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-y-auto" data-testid="view-bilan-dialog">
                    {viewPerson && (() => {
                        const fv = viewPerson.fiche_victime || {};
                        const constList = Array.isArray(fv.constantes) ? fv.constantes : [];
                        const evacConfig = evacuationStatuses.find(s => s.value === viewPerson.evacuation_status) || evacuationStatuses[0];
                        const centerName = centers.find(c => c.id === viewPerson.center_id)?.name;
                        const missionName = viewPerson.mission_name || missions.find(m => m.id === viewPerson.mission_id)?.title;
                        return (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="text-xl font-semibold text-white flex items-center gap-3">
                                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded font-mono text-sm">{fv.victime_numero || '-'}</span>
                                        {viewPerson.first_name} {viewPerson.last_name}
                                        {fv.sexe && <span className="text-xs text-zinc-400">({fv.sexe})</span>}
                                    </DialogTitle>
                                </DialogHeader>

                                <div className="space-y-4">
                                    {/* Identity row */}
                                    <div className="flex flex-wrap gap-3 text-sm">
                                        <span className={`status-badge ${evacConfig.color}`}>{evacConfig.label}</span>
                                        {centerName && <span className="text-cyan-400 text-xs flex items-center gap-1"><Building2 className="w-3 h-3" />{centerName}</span>}
                                        {missionName && <span className="text-amber-400 text-xs flex items-center gap-1"><Target className="w-3 h-3" />{missionName}</span>}
                                        {viewPerson.birth_date && <span className="text-zinc-400">Naissance: {isoToFr(viewPerson.birth_date)}</span>}
                                        {fv.heure_prise_en_charge && <span className="text-zinc-400">PEC: {fv.heure_prise_en_charge}</span>}
                                    </div>

                                    {/* Circonstance */}
                                    {fv.circonstance && (
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Circonstance</p>
                                            <p className="text-sm text-white">{fv.circonstance}</p>
                                        </div>
                                    )}

                                    {/* Signes */}
                                    {fv.signes && (
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Signes</p>
                                            <p className="text-sm text-white">{fv.signes}</p>
                                        </div>
                                    )}

                                    {/* Constantes vitales */}
                                    {constList.length > 0 && (
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-2 font-semibold uppercase">Constantes vitales ({constList.length} releves)</p>
                                            <div className="overflow-x-auto rounded border border-zinc-700/50">
                                                <table className="w-full text-xs">
                                                    <thead><tr className="bg-zinc-800/80 text-zinc-400">
                                                        <th className="px-2 py-1.5 text-left">Heure</th>
                                                        <th className="px-2 py-1.5 text-left">FC</th>
                                                        <th className="px-2 py-1.5 text-left">TA</th>
                                                        <th className="px-2 py-1.5 text-left">FR</th>
                                                        <th className="px-2 py-1.5 text-left">SAT</th>
                                                        <th className="px-2 py-1.5 text-left">Gly</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {constList.map((c, i) => (
                                                            <tr key={`${c.heure || ''}-${i}`} className="border-t border-zinc-700/30">
                                                                <td className="px-2 py-1.5 font-mono text-white">{c.heure || '-'}</td>
                                                                <td className="px-2 py-1.5">{c.fc || '-'}</td>
                                                                <td className="px-2 py-1.5">{c.ta || '-'}</td>
                                                                <td className="px-2 py-1.5">{c.fr || '-'}</td>
                                                                <td className="px-2 py-1.5">{c.sat || '-'}</td>
                                                                <td className="px-2 py-1.5">{c.gly || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Gestes */}
                                    {fv.gestes_effectues && (
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Gestes effectues</p>
                                            <p className="text-sm text-white">{fv.gestes_effectues}</p>
                                        </div>
                                    )}

                                    {/* Douleur + Lesions + Evolution + Devenir */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Douleur</p>
                                            <div className="flex items-center gap-1">
                                                {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
                                                    const val = fv.douleur ?? 0;
                                                    const bg = n <= 2 ? 'bg-blue-500' : n <= 4 ? 'bg-green-500' : n <= 6 ? 'bg-yellow-500' : n <= 8 ? 'bg-orange-500' : 'bg-red-500';
                                                    return <div key={n} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center ${n <= val ? `${bg} text-white` : 'bg-zinc-800 text-zinc-600'}`}>{n}</div>;
                                                })}
                                            </div>
                                        </div>
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Evolution</p>
                                            <span className={`text-sm font-medium ${fv.evolution === 'amelioration' ? 'text-green-400' : fv.evolution === 'aggravation' ? 'text-red-400' : 'text-amber-400'}`}>
                                                {fv.evolution === 'amelioration' ? 'Amelioration' : fv.evolution === 'aggravation' ? 'Aggravation' : fv.evolution === 'inchange' ? 'Inchange' : '-'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        {(fv.lesions?.length > 0 || fv.lesions_notes) && (
                                            <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                                <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Lesions</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {(fv.lesions || []).map(l => <span key={l} className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">{l}</span>)}
                                                </div>
                                                {fv.lesions_notes && <p className="text-xs text-zinc-300 mt-1">{fv.lesions_notes}</p>}
                                            </div>
                                        )}
                                        {(fv.devenir?.length > 0) && (
                                            <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                                <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Devenir</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {(fv.devenir || []).map(d => <span key={d} className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs uppercase">{d}</span>)}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Notes medicales */}
                                    {viewPerson.medical_notes && (
                                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                                            <p className="text-xs text-zinc-500 mb-1 font-semibold uppercase">Notes medicales</p>
                                            <p className="text-sm text-white">{viewPerson.medical_notes}</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PersonsPage;
